/* eslint-disable @typescript-eslint/no-unused-vars */
import { BaseRecord, CrudFilters } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import {
  AutoComplete as AntdAutoComplete,
  AutoCompleteProps as AntdAutoCompleteProps,
  SelectProps as AntdSelectProps,
  Button,
  Space,
  Typography,
} from "antd";
import debounce from "lodash.debounce";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { httpClient, ROOT_URL } from "../../../App";
import {
  dropdownSpaceButtonStyles,
  dropdownSpaceStyles,
  dropdownStyles,
} from "./styles";

export interface Option<T> extends BaseRecord {
  value: string | number;
  label: ReactNode;
  data: T;
}

export interface AutoCompleteProps<T>
  extends AntdSelectProps<T, Option<T>>,
    AntdAutoCompleteProps<T, Option<T>> {
  dropdownButtonLabel?: string;
  onClickDropdownButton?: VoidFunction;
  resource?: string;
  mapItem: (data: T, index: number) => Option<T>;
  refetchOnEmptyValue?: boolean;
  additionalFilters?: CrudFilters;
  disabled?: boolean;
  onClear?: () => void;
  notFoundPlaceholder?: string;
  params?: string | number;
}
// TODO: rework cleaner, separate data mapping from label render, debounce, trim
export const AutoCompleteNetwork = <T extends BaseRecord>({
  dropdownButtonLabel,
  onClickDropdownButton,
  resource,
  mapItem,
  children,
  refetchOnEmptyValue,
  additionalFilters = [],
  disabled = false,
  onClear,
  notFoundPlaceholder = "Not found",
  params,
  ...props
}: AutoCompleteProps<T>) => {
  const [value, setValue] = useState<string | number>();
  const [options, setOptions] = useState<Option<T>[]>([]);

  const debouncedSetValue = useMemo(
    () => debounce(setValue, 500, { trailing: true }),
    []
  );

  const [prevInputValue, setPrevInputValue] = useState<string | undefined>();

  const { refetch } = useQuery<T>(
    [resource],
    async () => {
      if (params) {
        const response = await httpClient.get(
          `${ROOT_URL}/${resource}?_currencyId=${params}`
        );
        return response.data;
      }
      const response = await httpClient.get(`${ROOT_URL}/${resource}`);
      return response.data;
    },
    {
      enabled: !disabled,
      onSuccess: (data: any) => {
        setOptions(data?.map(mapItem));
      },
    }
  );

  useEffect(() => {
    if (params) refetch();
  }, [params, refetch]);

  useEffect(() => {
    if (!refetchOnEmptyValue && !value) {
      setOptions([]);
    } else {
      if (!disabled) {
        refetch();
      }
    }
  }, [value, refetchOnEmptyValue, disabled, refetch]);

  const onInputKeyDown = useCallback((e: any) => {
    setPrevInputValue(e.target.value);
  }, []);

  const onSearch = useCallback(
    (newValue: string) => {
      if (
        typeof prevInputValue === "string" &&
        newValue.trimEnd() !== prevInputValue?.trimEnd()
      ) {
        onClear?.();
      }
      setPrevInputValue(undefined);
      debouncedSetValue(newValue);
    },
    [prevInputValue, debouncedSetValue, onClear]
  );

  return (
    <AntdAutoComplete
      {...props}
      filterOption={false}
      options={options}
      onSearch={onSearch}
      dropdownStyle={dropdownStyles}
      disabled={disabled}
      notFoundContent={
        <Typography.Text style={{ padding: "10px" }}>
          {notFoundPlaceholder}
        </Typography.Text>
      }
      onInputKeyDown={onInputKeyDown}
      onClear={onClear}
      onSelect={(value, data) => {
        debouncedSetValue(data.value);
        props.onSelect?.(value, data);
      }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {dropdownButtonLabel && (
            <Space direction="horizontal" style={dropdownSpaceStyles}>
              <Button
                type="text"
                style={dropdownSpaceButtonStyles}
                onClick={onClickDropdownButton}
              >
                {dropdownButtonLabel}
              </Button>
            </Space>
          )}
        </>
      )}
    >
      {children}
    </AntdAutoComplete>
  );
};
